<template>
  <div class="performance">
    <div class="column">
      <div class="brand-image">
        <image-preview
          :loading="adInfoLoading"
          :previewUrl="imageUrl">
        </image-preview>
        <div>{{adName}}</div>
      </div>
    </div>
    <div class="column">
      <div class="row top">
        <div class="logs-row">
          <performance-log
            key="retailersLog"
            :currentAdvertiser="currentAdvertiser"
            :apiParams="apiParams"
            :availableRetailers="availableRetailers"
            extraClasses="retailers-logs"
            header="retailers log"
            logType="retailers"
            shouldReturnLogData
            url="performance/retailers_log"
            @setLog="setLog"
            @setSelectedLogToView="setSelectedLogToView" />
          <performance-log
            key="visitorsLog"
            :apiParams="apiParams"
            extraClasses="visitors-logs"
            header="visitors log"
            logType="visitors"
            url="performance/visitors_log" />
        </div>

        <retailer-filter
          :retailers="availableRetailers"
          @setSelectedRetailerIds="setSelectedRetailerIds" />

        <div class="edit-icon" v-if="!expired" @click="setConfirmModal">
          <fai :icon="['fas', 'pen-square']" size="3x"></fai>
          <span>edit ad</span>
        </div>

        <modal
          v-if="showModal"
          :buttonText="modalConfig.buttonText"
          :showClose="modalConfig.showClose"
          :wrapperClasses="modalConfig.wrapperClasses"
          @close="showModal = false"
          @proceed="processModalAction(modalConfig.action)">
          <div slot="header" class="header">{{modalConfig.text}}</div>
        </modal>
      </div>

      <performance-kpis
        :apiParams="apiParams"
        :advertiserId="currentAdvertiser.id"
        :retailerIds="selectedRetailerIds"
        :smsRate="smsRate" />

      <report-graphs
        :adId="adId"
        :advertiserTimezone="currentAdvertiser.time_zone"
        :apiParams="apiParams"
        :advertiserId="currentAdvertiser.id"
        :retailerIds="selectedRetailerIds" />

      <performance-log-details
        :logDetails="retailersLog"
        :selectedLogToView="selectedLogToView"
        @setSelectedLogToView="setSelectedLogToView" />
    </div>
  </div>
</template>

<script>
  import ImagePreview from '../Shared/ImagePreview'
  import moment from 'moment'
  import Modal from '../Shared/Modal'
  import PerformanceKpis from './PerformanceKpis.vue'
  import PerformanceLog from './PerformanceLog.vue'
  import PerformanceLogDetails from './PerformanceLogDetails.vue'
  import ReportGraphs from '../Reports/ReportGraphs'
  import RetailerFilter from './RetailerFilter'
  import { adAPI } from '../../services/api/adAPI'

  export default {
    props: {
      currentAdvertiser: Object,
    },

    name: 'Performance',

    components: {
      ImagePreview,
      Modal,
      PerformanceKpis,
      PerformanceLog,
      PerformanceLogDetails,
      ReportGraphs,
      RetailerFilter,
    },

    data() {
      return {
        adError: null,
        adId: null,
        adInfoLoading: false,
        adName: null,
        adSentAt: null,
        availableRetailers: [],
        delayedClearSelectedLogToView: null,
        endOfToday: moment.utc().endOf('day').unix(),
        imageUrl: null,
        modalConfig: {},
        retailersLog: null,
        selectedLogToView: null,
        selectedRetailerIds: [],
        showModal: false,
        smsRate: null,
        visitorsLog: null,
      }
    },

    methods: {
      async loadAdInfo() {
        this.adInfoLoading = true
        try {
          const ad = await adAPI.getAd(this.currentAdvertiser.id, this.adId)
          this.configureAd(ad)
        } catch(error) {
          this.configureAd(null, error)
        } finally {
          this.adInfoLoading = false
        }
      },

      configureAd(data, error) {
        if (data) {
          this.adName = data.name
          this.adSentAt = data.send_at_end
          this.imageUrl = data.attachments[0].url
          this.smsRate = data.cost_per_message
          this.adError = null
        } else {
          let errorText = 'there was an issue loading the ad'
          this.adName = null
          this.imageUrl = null
          this.smsRate = null
          this.adError = error ? `${errorText}: ${error}` : `${errorText}, please refresh or contact support`
        }
      },

      async getRetailers() {
        const errorText = 'there was an error loading retailers'
        try {
          const retailers = await adAPI.getAdRetailers(this.apiParams.params)
          if (retailers && typeof retailers !== 'string') {
            this.availableRetailers = retailers
            this.adError = null
          } else {
            this.adError = `${errorText}, please refresh or contact support`
          }
        } catch(error) {
          this.adError = `${errorText}: ${error}`
        }
      },

      processModalAction(action) {
        if (action === 'editAd') {
          return this.$router.push( { path: `/ad/${this.adId}` } )
        } else if (action === 'reloadAfterError') {
          this.reloadAfterError()
        }
      },

      reloadAfterError() {
        this.showModal = false
        this.loadAdInfo()
      },

      setConfirmModal() {
        this.modalConfig = {
          buttonText: 'continue',
          text: "changes made to this ad will take immediate effect",
          wrapperClasses: 'warning-modal',
          action: 'editAd',
          showClose: true
        }
        this.showModal = true
      },

      setErrorModal() {
        this.modalConfig = {
          buttonText: 'reload ads',
          text: 'there was an issue ending this ad, please try again',
          wrapperClasses: 'warning-modal error',
          action: 'reloadAfterError'
        }
        this.showModal = true
      },

      setLog(logData, logType) {
        this[`${logType}Log`] = logData
      },

      setSelectedLogToView(logId) {
        this.selectedLogToView = logId
      },

      setSelectedRetailerIds(selectedIds) {
        const bothEmpty = !this.selectedRetailerIds?.length && !selectedIds?.length

        // Occurs on first load when the retailers dropdown loads
        if (!bothEmpty) {
          this.selectedRetailerIds = selectedIds
        }
      },
    },

    created() {
      this.adId = this.$route.params.id
      this.loadAdInfo()
      this.getRetailers()
    },

    computed: {
      apiParams: function() {
        return {
          params: {
            brand_id: this.currentAdvertiser.id,
            brand_campaign_id: this.adId,
            merchant_id: this.selectedRetailerIds
          }
        }
      },
      expired: function () {
        return this.endOfToday > moment(this.adSentAt).unix()
      },
    },
  }
</script>

<style lang="sass">
@import '../../assets/sass/colors'
.performance
  color: $title-color
  padding: 1rem 6rem
  text-align: center
  display: flex
  justify-content: space-around

  .column:last-child
    display: flex
    flex-flow: row wrap

  .column:last-child > div
    flex-basis: 100%

  .column
    .brand-image,
    .ad-image
      font-size: .9rem

    img
      border: 1px solid $primary-color
      height: auto
      max-width: 250px
      max-height: none

  .row.top
    display: flex
    justify-content: space-between
    min-height: 118px

    .edit-icon
      margin-top: -10px
      color: $primary-color
      cursor: pointer
      padding-left: 30px
      span
        font-size: 0.75em
      svg
        display: block


  .report-graphs-section,
  .log-details
    margin: 0 8%

  .log-details
    max-width: 1000px

  .logs-row
    border-bottom: 1px solid $primary-transparent
    display: flex
    flex: 1
    padding: 1rem 2rem 0 2rem
    max-width: 58.6%
    margin: 0 7%

  .kpis-row
    display: flex
    justify-content: space-around
    margin: 2rem 4rem

  .performance-kpis .kpi-row
    margin: 2rem 8%

  .bar-charts
    display: flex
    justify-content: space-around
    padding: 2rem 4rem

  .trendline-chart
    padding: 2rem 4rem

</style>
